import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, DollarSign, Clock } from "lucide-react";

const caseStudyList = [
  {
    id: 1,
    slug: "techflow-series-b",
    title: "TechFlow AI Series B Success",
    category: "Startup Advisory",
    description: "How we helped an AI startup secure ₹45Cr in Series B funding with a compelling financial narrative and investor-ready documentation.",
    image: "https://images.unsplash.com/photo-1692607520918-7d8bb85e7e64?w=800&q=80",
    stat1Label: "Raised",
    stat1Value: "₹45Cr",
    stat2Label: "Growth",
    stat2Value: "3x",
    stat3Label: "Time to Close",
    stat3Value: "4mo",
    result1: "Restructured financial model to highlight unit economics",
    result2: "Developed compelling pitch narrative for institutional investors",
  },
  {
    id: 2,
    slug: "nexus-health-tax-strategy",
    title: "Nexus Health Tax Optimization",
    category: "Tax & Investments",
    description: "Strategic tax planning that saved a healthcare startup's founding team over ₹2.3Cr through QSBS optimization and equity restructuring.",
    image: "https://images.unsplash.com/photo-1734971702269-60d36fc28829?w=800&q=80",
    stat1Label: "Tax Saved",
    stat1Value: "₹2.3Cr",
    stat2Label: "QSBS Qualified",
    stat2Value: "100%",
    stat3Label: "Timeline",
    stat3Value: "6mo",
    result1: "Achieved full QSBS qualification for all founders",
    result2: "Restructured equity grants for tax efficiency",
  },
  {
    id: 3,
    slug: "greenscale-operational-transformation",
    title: "GreenScale Operational Excellence",
    category: "Business Consulting",
    description: "Transforming a climate-tech company's operations to achieve 50% efficiency gains and support 10x growth trajectory.",
    image: "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?w=800&q=80",
    stat1Label: "Efficiency Gain",
    stat1Value: "50%",
    stat2Label: "Cost Reduction",
    stat2Value: "₹80L",
    stat3Label: "Implementation",
    stat3Value: "3mo",
    result1: "Redesigned core operational workflows",
    result2: "Implemented automated reporting systems",
  },
  {
    id: 4,
    slug: "quantum-ventures-cfo",
    title: "Quantum Ventures CFO Partnership",
    category: "CFO Services",
    description: "Providing fractional CFO leadership to a fintech company through hyper-growth, from Series A to acquisition.",
    image: "https://images.unsplash.com/photo-1762279389002-7b6abd7bd6c6?w=800&q=80",
    stat1Label: "Exit Value",
    stat1Value: "₹120Cr",
    stat2Label: "ARR Growth",
    stat2Value: "5x",
    stat3Label: "Partnership",
    stat3Value: "2yrs",
    result1: "Led financial strategy through 3 funding rounds",
    result2: "Managed due diligence for strategic acquisition",
  },
];

export default function CaseStudiesPage() {
  return (
    <main data-testid="case-studies-page" className="pt-24 md:pt-32 bg-[#050505]">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Success Stories
          </span>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Case Studies
          </h1>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl">
            Real results from real partnerships. Explore how we've helped founders 
            achieve their financial goals.
          </p>
        </motion.div>
      </section>

      {/* Case Studies Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {caseStudyList.map((study, index) => (
            <motion.article
              key={study.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`case-study-card-${study.slug}`}
              className="group bg-[#0A0A0A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00C896]/20 transition-all"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={study.image}
                  alt={study.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                <span className="absolute bottom-4 left-6 text-xs font-mono tracking-wider uppercase text-[#00C896]">
                  {study.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                <h2
                  className="text-2xl font-semibold text-white mb-3 group-hover:text-[#00C896] transition-colors"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {study.title}
                </h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  {study.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#00C896]">{study.stat1Value}</p>
                    <p className="text-xs text-slate-500">{study.stat1Label}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#00C896]">{study.stat2Value}</p>
                    <p className="text-xs text-slate-500">{study.stat2Label}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#00C896]">{study.stat3Value}</p>
                    <p className="text-xs text-slate-500">{study.stat3Label}</p>
                  </div>
                </div>

                {/* Results Preview */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-start gap-2 text-sm text-slate-400">
                    <span className="text-[#00C896] mt-1">•</span>
                    {study.result1}
                  </div>
                  <div className="flex items-start gap-2 text-sm text-slate-400">
                    <span className="text-[#00C896] mt-1">•</span>
                    {study.result2}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-[#00C896] font-medium group-hover:gap-3 transition-all">
                  <span>Read Full Case Study</span>
                  <ArrowRight size={16} />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Ready to Write Your Success Story?
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-10">
              Join the ranks of successful founders who've transformed their 
              financial trajectory with Finease.
            </p>
            <Link
              to="/contact"
              data-testid="case-studies-cta"
              className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-10 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)]"
            >
              Start Your Journey
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
